import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "../customAxios.js";

/**
 * @typedef {object} DeliveryItemsPagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} DeliveryItemsFilters
 * @property {string} search
 */

/**
 * Initializes state from localStorage.
 * @returns {{filters: DeliveryItemsFilters, pagination: DeliveryItemsPagination}}
 */

// Auxiliary function to initialize filters from localStorage, outside the hook.
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("deliveryItemsFilter");
  const defaults = {
    filters: {
      search: "",
    },
    pagination: {
      page: 1,
      rowsPerPage: 20,
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    return {
      filters: {
        search: savedState.search || defaults.filters.search,
      },
      pagination: defaults.pagination,
    };
  }
  return defaults;
};

/**
 * Custom Hook to fetch and manage a paginated/searchable list of delivery items.
 *
 * @returns {{
 * deliveryItems: Array<object>,
 * loading: boolean,
 * error: string | null,
 * auxSearcher: string,
 * pagination: DeliveryItemsPagination,
 * filters: DeliveryItemsFilters,
 * deliveryItemsCount: number,
 * setAuxSearcher: React.Dispatch<React.SetStateAction<string>>,
 * handleFilterChange: (name: string, value: any) => void,
 * handleChangePage: (newPage: number) => void,
 * handleChangeRowsPerPage: (newRows: number) => void,
 * refetchDeliveryItems: () => Promise<void>
 * }}
 */

export function useDeliveryItems() {
  // Data status and IU
  const { orderId } = useParams();

  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [auxSearcher, setAuxSearcher] = useState(filters.search);
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [deliveryItemsCount, setDeliveryItemsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const fetchDeliveryItems = useCallback(async (order) => {
    setLoading(true);
    setError(null);
    const params = {
      search: filters.search || undefined,
      page: pagination.page,
      page_size: pagination.rowsPerPage,
    };
    try {
      const {
        data: { count, results: deliveryItems },
      } = await axios.get(`deliveries/${orderId || order}/delivery_items/list/`, {
        params,
      });
      setDeliveryItems(deliveryItems || []);
      setDeliveryItemsCount(count || 0);
    } catch (error) {
      console.error(`Envio no tiene bultos asociados: ${error}`);
      setError("Envio no tiene bultos asociados");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, orderId]);

  const createDeliveryItem = useCallback(
    async (item) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post(`deliveries/${orderId}/delivery_items/create/`, item);
        await fetchDeliveryItems();
      } catch (error) {
        console.error(`Hubo un error al crear el bulto: ${error}`);
        setError("Hubo un error al crear el bulto");
      } finally {
        setLoading(false);
      }
    },
    [fetchDeliveryItems],
  );

  const deleteDeliveryItem = useCallback(
    async (itemId) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(`deliveries/delivery_items/${itemId}/delete/`);
        await fetchDeliveryItems();
      } catch (error) {
        console.error(`Hubo un error al eliminar el bulto: ${error}`);
        setError("Hubo un error al eliminar el bulto");
      } finally {
        setLoading(false);
      }
    },
    [fetchDeliveryItems],
  );

  const updateDeliveryItemStatus = useCallback(
    async (itemId, status, orderId) => {
      setLoading(true);
      setError(null);
      try {
        await axios.patch(`deliveries/delivery_items/${itemId}/update_status/`, {status});
        await fetchDeliveryItems(orderId);
      } catch (error) {
        console.error(`Hubo un error al actualizar el estado del bulto: ${error}`);
        setError("Hubo un error al actualizar el estado del bulto");
      } finally {
        setLoading(false);
      }
    },
    [fetchDeliveryItems],
  );

  const setItemsCount = useCallback(
    async (count) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.post(
          `deliveries/${orderId}/delivery_items/set_count/`,
          { count },
        );
        setDeliveryItems(data.items || []);
        setDeliveryItemsCount(data.count ?? count);
      } catch (error) {
        console.error(`Error al establecer cantidad de bultos: ${error}`);
        setError("Error al establecer la cantidad de bultos");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [orderId],
  );

  const updateDeliveryItemLogistics = useCallback(async (itemId, data) => {
    const { data: updatedItem } = await axios.patch(
      `deliveries/delivery_items/${itemId}/update_logistics/`,
      data,
    );
    setDeliveryItems((prev) =>
      prev.map((item) => (item.id === itemId ? updatedItem : item)),
    );
    return updatedItem;
  }, []);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (auxSearcher !== filters.search) {
        handleFilterChange("search", auxSearcher);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [auxSearcher, filters.search, handleFilterChange]);

  useEffect(() => {
    localStorage.setItem(
      "deliveryItemsFilter",
      JSON.stringify({
        ...filters,
      }),
    );
  }, [filters]);

  useEffect(() => {
    fetchDeliveryItems();
  }, [fetchDeliveryItems]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    deliveryItems,
    loading,
    error,
    auxSearcher,
    pagination,
    filters,
    deliveryItemsCount,

    // MANNERS (Setters y Actions)
    setDeliveryItemsCount,
    setAuxSearcher,
    handleFilterChange,
    handleChangePage,
    handleChangeRowsPerPage,
    refetchDeliveryItems: fetchDeliveryItems,
    createDeliveryItem,
    deleteDeliveryItem,
    updateDeliveryItemStatus,
    updateDeliveryItemLogistics,
    setItemsCount,
  };
}
